import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TicketingAuthService } from '../services/ticketing-auth.service';
import Swal from 'sweetalert2';

// Requests that legitimately return 401 for bad credentials (not an
// expired/invalid session) — must not trigger an auto-logout redirect.
const AUTH_ENDPOINTS = ['ticketing/login', 'ticketing/sso/validate'];

// Attaches the ticketing session token to every outgoing request — previously
// nothing did this, so every /ticketing/* call went out unauthenticated at
// the transport layer. Raw token, no "Bearer " prefix, matching the backend's
// convention (see authenticateTicketingUser in HandyPro-Backend).
//
// Also handles the other half of the token lifecycle: the backend signs
// session tokens with a 1-hour expiry, but nothing was reacting when that
// expiry (or any other invalid-token case) came back as a 401 — the app just
// kept silently failing every request while stale data sat in localStorage.
// Now a real 401 clears the session and sends the user back to login.
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private authService: TicketingAuthService, private router: Router) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authService.token;
    if (token) {
      req = req.clone({ setHeaders: { authorization: token } });
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const isAuthEndpoint = AUTH_ENDPOINTS.some((path) => req.url.includes(path));
        if (error.status === 401 && !isAuthEndpoint && this.authService.isLoggedIn()) {
          this.authService.logout();
          Swal.fire({
            icon: 'warning',
            title: 'Session expired',
            text: 'Please log in again.',
            timer: 2500,
            showConfirmButton: false
          });
          this.router.navigate(['/authentication/login']);
        }
        return throwError(error);
      })
    );
  }
}
